import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Invoice {
  id: number;
  name: string;
  category: string;
  amount: number;
  due: string;
  week: string;
  terms: string | null;
  aprRate: number; // 0 if no early-payment discount terms
}

type Phase = "idle" | "reviewing" | "deploying" | "done";
type NegotiationPhase = "idle" | "negotiating" | "done";

// ─── Sample invoice data (representative 12 of 1,412 invoices) ───────────────

const SAMPLE_INVOICES: Invoice[] = [
  {
    id: 1,
    name: "Midwest Steel Supply",
    category: "Raw Materials",
    amount: 487200,
    due: "Apr 8",
    week: "Apr 7",
    terms: "2% 10 Net 30",
    aprRate: 2.0,
  },
  {
    id: 2,
    name: "Applied Components Inc",
    category: "Components",
    amount: 231500,
    due: "Apr 11",
    week: "Apr 7",
    terms: "1.8% APR",
    aprRate: 1.8,
  },
  {
    id: 3,
    name: "National Hydraulics",
    category: "Systems",
    amount: 398600,
    due: "Apr 22",
    week: "Apr 21",
    terms: "2% 15 Net 45",
    aprRate: 2.0,
  },
  {
    id: 4,
    name: "Summit Fabrication",
    category: "Fabrication",
    amount: 623000,
    due: "Apr 15",
    week: "Apr 14",
    terms: "1.5% 10 Net 30",
    aprRate: 1.5,
  },
  {
    id: 5,
    name: "Precision Tooling Co",
    category: "Tooling",
    amount: 154800,
    due: "Apr 28",
    week: "Apr 28",
    terms: "1.75% APR",
    aprRate: 1.75,
  },
  {
    id: 6,
    name: "ProTech Systems",
    category: "Systems",
    amount: 312000,
    due: "May 6",
    week: "May 5",
    terms: "2.5% 10 Net 30",
    aprRate: 2.5,
  },
  {
    id: 7,
    name: "Delta Machining",
    category: "Machining",
    amount: 189400,
    due: "May 2",
    week: "Apr 28",
    terms: "1.6% APR",
    aprRate: 1.6,
  },
  {
    id: 8,
    name: "Vertex Materials",
    category: "Materials",
    amount: 456200,
    due: "May 8",
    week: "May 5",
    terms: null,
    aprRate: 0,
  },
  {
    id: 9,
    name: "Advanced Electronics",
    category: "Electronics",
    amount: 302400,
    due: "Apr 18",
    week: "Apr 14",
    terms: null,
    aprRate: 0,
  },
  {
    id: 10,
    name: "Pacific Bearings Co",
    category: "Components",
    amount: 182400,
    due: "Apr 23",
    week: "Apr 21",
    terms: null,
    aprRate: 0,
  },
  {
    id: 11,
    name: "American Industrial",
    category: "Industrial",
    amount: 741200,
    due: "Apr 30",
    week: "Apr 28",
    terms: null,
    aprRate: 0,
  },
  {
    id: 12,
    name: "Northwest Coatings",
    category: "Coatings",
    amount: 521500,
    due: "May 14",
    week: "May 12",
    terms: null,
    aprRate: 0,
  },
];

// ─── Aggregate pool data (full 1,412-invoice universe) ────────────────────────

const TOTAL_INVOICE_COUNT = 1_412;
const TOTAL_AP_6WK = 23_400_000;
const BUDGET_DEFAULT = 1_000_000;

const APR_STEPS = [1.0, 1.25, 1.5, 1.75, 2.0, 2.25, 2.5];

interface PoolStats {
  count: number;
  amount: number;
  blendedApr: number;
}

const APR_POOL: Record<number, PoolStats> = {
  1.0: { count: 1089, amount: 18_200_000, blendedApr: 1.58 },
  1.25: { count: 847, amount: 13_900_000, blendedApr: 1.69 },
  1.5: { count: 612, amount: 9_200_000, blendedApr: 1.81 },
  1.75: { count: 398, amount: 5_800_000, blendedApr: 1.93 },
  2.0: { count: 203, amount: 2_800_000, blendedApr: 2.14 },
  2.25: { count: 89, amount: 1_100_000, blendedApr: 2.31 },
  2.5: { count: 22, amount: 280_000, blendedApr: 2.58 },
};

function getPoolStats(aprFloor: number): PoolStats {
  const key = APR_STEPS.filter((s) => aprFloor >= s).at(-1) ?? 1.0;
  return APR_POOL[key];
}

function getDeployStats(aprFloor: number, budget: number) {
  const pool = getPoolStats(aprFloor);
  const deployedAmt = Math.min(pool.amount, budget);
  const frac = pool.amount > 0 ? deployedAmt / pool.amount : 0;
  const deployedCount = Math.round(pool.count * frac);
  const deployedYield = Math.round(deployedAmt * (pool.blendedApr / 100) * (30 / 365));
  return {
    qualifyingCount: pool.count,
    qualifyingAmt: pool.amount,
    blendedApr: pool.blendedApr,
    deployedAmt,
    deployedCount,
    deployedYield,
    budgetConstrained: budget < pool.amount,
  };
}

// ─── Supplier profiles (Suppliers tab — each has multiple invoices) ────────────

interface SupplierProfile {
  name: string;
  category: string;
  invoices: {
    id: string;
    amount: number;
    due: string;
    terms: string | null;
  }[];
}

const SUPPLIER_PROFILES: SupplierProfile[] = [
  {
    name: "Midwest Steel Supply",
    category: "Raw Materials",
    invoices: [
      { id: "INV-4821", amount: 487200, due: "Apr 8", terms: "2% 10 Net 30" },
      { id: "INV-4756", amount: 156000, due: "Apr 22", terms: null },
      { id: "INV-4699", amount: 89400, due: "May 6", terms: null },
    ],
  },
  {
    name: "Applied Components Inc",
    category: "Components",
    invoices: [
      { id: "INV-3302", amount: 231500, due: "Apr 11", terms: "1.8% APR" },
      { id: "INV-3278", amount: 178300, due: "Apr 25", terms: null },
    ],
  },
  {
    name: "Summit Fabrication",
    category: "Fabrication",
    invoices: [
      { id: "INV-7641", amount: 623000, due: "Apr 15", terms: "1.5% 10 Net 30" },
      { id: "INV-7588", amount: 245000, due: "May 1", terms: null },
    ],
  },
  {
    name: "National Hydraulics",
    category: "Systems",
    invoices: [
      { id: "INV-2201", amount: 398600, due: "Apr 22", terms: "2% 15 Net 45" },
      { id: "INV-2188", amount: 167200, due: "May 8", terms: "Net 60" },
    ],
  },
  {
    name: "Precision Tooling Co",
    category: "Tooling",
    invoices: [{ id: "INV-5512", amount: 154800, due: "Apr 28", terms: "1.75% APR" }],
  },
  {
    name: "Vertex Materials",
    category: "Materials",
    invoices: [
      { id: "INV-8834", amount: 456200, due: "May 8", terms: null },
      { id: "INV-8801", amount: 112000, due: "May 15", terms: null },
    ],
  },
  {
    name: "Advanced Electronics",
    category: "Electronics",
    invoices: [
      { id: "INV-1102", amount: 302400, due: "Apr 18", terms: null },
      { id: "INV-1089", amount: 198000, due: "May 3", terms: "2% 10 Net 45" },
    ],
  },
  {
    name: "Coastal Packaging",
    category: "Packaging",
    invoices: [{ id: "INV-6643", amount: 78900, due: "Apr 25", terms: null }],
  },
];

// ─── Negotiation targets (no-terms suppliers for Act lever) ───────────────────

const NEGOTIATION_TARGETS = SAMPLE_INVOICES.filter((i) => i.aprRate === 0).map((inv, idx) => ({
  ...inv,
  potentialApr: ["1.5%", "2%", "1.8%", "1.5%", "2%"][idx % 5],
  agreementType: idx < 2 ? "one-time" : idx < 4 ? "dynamic" : null,
}));

// ─── Weekly chart data (6-week aggregate) ────────────────────────────────────

const WEEK_BARS = [
  { label: "Apr 7", total: 4_180_000, eligibleAmt: 1_820_000 },
  { label: "Apr 14", total: 5_760_000, eligibleAmt: 2_380_000 },
  { label: "Apr 21", total: 3_890_000, eligibleAmt: 1_610_000 },
  { label: "Apr 28", total: 4_720_000, eligibleAmt: 1_940_000 },
  { label: "May 5", total: 2_580_000, eligibleAmt: 1_100_000 },
  { label: "May 12", total: 2_270_000, eligibleAmt: 960_000 },
];

const MAX_BAR = Math.max(...WEEK_BARS.map((b) => b.total));

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}

function fmtFull(n: number) {
  return "$" + n.toLocaleString();
}

// ─── WeeklyChart ─────────────────────────────────────────────────────────────

function WeeklyChart({ deployed }: { deployed: boolean }) {
  return (
    <div>
      <div className="flex items-end gap-2 h-40">
        {WEEK_BARS.map((bar) => {
          const barH = Math.round((bar.total / MAX_BAR) * 160);
          const eligibleH = Math.round((bar.eligibleAmt / bar.total) * barH);
          const otherH = barH - eligibleH;

          return (
            <div key={bar.label} className="flex-1 flex flex-col items-center gap-1">
              <div
                className="w-full flex flex-col justify-end rounded-t overflow-hidden"
                style={{ height: `${barH}px` }}
              >
                <div
                  className={`w-full transition-colors duration-700 ${deployed ? "bg-orange-400" : "bg-green-400"}`}
                  style={{ height: `${eligibleH}px` }}
                />
                <div className="w-full bg-slate-200" style={{ height: `${otherH}px` }} />
              </div>
              <span className="text-xs text-slate-400 whitespace-nowrap">{bar.label}</span>
              <span className="text-xs text-slate-500 font-medium">{fmt(bar.total)}</span>
            </div>
          );
        })}
      </div>
      <div className="flex gap-4 mt-3">
        <div className="flex items-center gap-1.5">
          <div
            className={`w-3 h-3 rounded-sm transition-colors duration-700 ${deployed ? "bg-orange-400" : "bg-green-400"}`}
          />
          <span className="text-xs text-slate-500">
            {deployed ? "Deployed early" : "Eligible for early payment"}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-slate-200" />
          <span className="text-xs text-slate-500">Standard terms</span>
        </div>
      </div>
    </div>
  );
}

// ─── InvoiceTable ─────────────────────────────────────────────────────────────

function InvoiceTable({
  invoices,
  aprFloor,
  phase,
}: {
  invoices: Invoice[];
  aprFloor: number;
  phase: Phase;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-100">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-100">
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Supplier
            </th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Category
            </th>
            <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Amount
            </th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Due
            </th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Terms
            </th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv, idx) => {
            const qualifying = inv.aprRate >= aprFloor && inv.aprRate > 0;
            const deployed = phase === "done" && qualifying;
            return (
              <tr
                key={inv.id}
                className={`border-b border-slate-50 transition-colors ${
                  deployed ? "bg-orange-50/60" : idx % 2 === 0 ? "bg-white" : "bg-slate-50/30"
                }`}
              >
                <td className="px-4 py-3 font-medium text-slate-800">{inv.name}</td>
                <td className="px-4 py-3 text-slate-500">{inv.category}</td>
                <td className="px-4 py-3 text-right font-medium text-slate-700">
                  {fmtFull(inv.amount)}
                </td>
                <td className="px-4 py-3 text-slate-500">{inv.due}</td>
                <td className="px-4 py-3">
                  {inv.terms ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                      {inv.terms}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {deployed ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">
                      ✓ Deployed
                    </span>
                  ) : qualifying ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                      Qualifies
                    </span>
                  ) : inv.terms ? (
                    <span className="text-xs text-slate-400">Below threshold</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                      Available
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── SupplierExplorer ─────────────────────────────────────────────────────────

function SupplierExplorer() {
  const [expandedSupplier, setExpandedSupplier] = useState<string | null>(null);

  return (
    <div className="overflow-hidden rounded-lg border border-slate-100">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-100">
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide w-6" />
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Supplier
            </th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Category
            </th>
            <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Total AP
            </th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Terms mix
            </th>
          </tr>
        </thead>
        <tbody>
          {SUPPLIER_PROFILES.map((supplier, idx) => {
            const totalAP = supplier.invoices.reduce((s, i) => s + i.amount, 0);
            const withDiscount = supplier.invoices.filter((i) => i.terms && i.terms.includes("%"));
            const withTerms = supplier.invoices.filter((i) => i.terms !== null);
            const isExpanded = expandedSupplier === supplier.name;

            return (
              <>
                <tr
                  key={supplier.name}
                  onClick={() => setExpandedSupplier(isExpanded ? null : supplier.name)}
                  className={`border-b border-slate-50 cursor-pointer transition-colors hover:bg-slate-50/60 ${
                    isExpanded ? "bg-slate-50/40" : idx % 2 === 0 ? "bg-white" : "bg-slate-50/30"
                  }`}
                >
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    <span
                      className="transition-transform inline-block"
                      style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
                    >
                      ▶
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800">{supplier.name}</td>
                  <td className="px-4 py-3 text-slate-500">{supplier.category}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-700">
                    {fmtFull(totalAP)}
                  </td>
                  <td className="px-4 py-3">
                    {withDiscount.length > 0 ? (
                      <span className="text-xs text-slate-600">
                        <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                          {withDiscount.length} with discount
                        </span>
                        {supplier.invoices.length - withDiscount.length > 0 && (
                          <span className="text-slate-400 ml-2">
                            · {supplier.invoices.length - withDiscount.length} without
                          </span>
                        )}
                      </span>
                    ) : withTerms.length > 0 ? (
                      <span className="text-xs text-slate-500">
                        {withTerms.length} with net terms ·{" "}
                        {supplier.invoices.length - withTerms.length} without
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                        Available for optimization
                      </span>
                    )}
                  </td>
                </tr>
                {isExpanded &&
                  supplier.invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-50 bg-slate-50/60">
                      <td className="px-4 py-2.5" />
                      <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">{inv.id}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-400">{inv.due}</td>
                      <td className="px-4 py-2.5 text-right text-xs text-slate-600 font-medium">
                        {fmtFull(inv.amount)}
                      </td>
                      <td className="px-4 py-2.5">
                        {inv.terms ? (
                          inv.terms.includes("%") ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                              {inv.terms}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">{inv.terms}</span>
                          )
                        ) : (
                          <span className="text-xs text-slate-400">No discount terms</span>
                        )}
                      </td>
                    </tr>
                  ))}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── RightPanel ───────────────────────────────────────────────────────────────

function RightPanel({
  phase,
  aprFloor,
  onAprChange,
  budget,
  onBudgetChange,
  deployedCount,
  onReview,
  onCancelReview,
  onConfirmDeploy,
}: {
  phase: Phase;
  aprFloor: number;
  onAprChange: (v: number) => void;
  budget: number;
  onBudgetChange: (value: number, isOneTime: boolean) => void;
  deployedCount: number;
  onReview: () => void;
  onCancelReview: () => void;
  onConfirmDeploy: () => void;
}) {
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const stats = getDeployStats(aprFloor, budget);

  function startBudgetEdit() {
    setBudgetInput(String(budget));
    setIsEditingBudget(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }

  function commitBudget(isOneTime: boolean) {
    const parsed = parseInt(budgetInput.replace(/[^0-9]/g, ""), 10);
    if (!isNaN(parsed) && parsed > 0) onBudgetChange(parsed, isOneTime);
    setIsEditingBudget(false);
  }

  const aprIdx = APR_STEPS.indexOf(aprFloor);
  const canDecrease = aprIdx > 0;
  const canIncrease = aprIdx < APR_STEPS.length - 1;

  const deployProgress = stats.deployedCount > 0 ? (deployedCount / stats.deployedCount) * 100 : 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Parameters card */}
      <Card className="shadow-sm border-slate-100">
        <CardHeader className="pb-2 pt-4 px-5">
          <span className="text-sm font-semibold text-slate-700">Early Payment Parameters</span>
        </CardHeader>
        <CardContent className="px-5 pb-4 flex flex-col gap-4">
          {/* Min APR */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-slate-500">Min. APR threshold</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => canDecrease && onAprChange(APR_STEPS[aprIdx - 1])}
                  disabled={!canDecrease || phase !== "idle"}
                  className="w-6 h-6 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 flex items-center justify-center text-sm leading-none"
                >
                  −
                </button>
                <span className="text-sm font-bold text-slate-800 w-12 text-center tabular-nums">
                  {aprFloor.toFixed(2)}%
                </span>
                <button
                  onClick={() => canIncrease && onAprChange(APR_STEPS[aprIdx + 1])}
                  disabled={!canIncrease || phase !== "idle"}
                  className="w-6 h-6 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 flex items-center justify-center text-sm leading-none"
                >
                  +
                </button>
              </div>
            </div>
            <div className="flex gap-1 flex-wrap">
              {APR_STEPS.map((s) => (
                <button
                  key={s}
                  onClick={() => phase === "idle" && onAprChange(s)}
                  disabled={phase !== "idle"}
                  className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
                    aprFloor === s
                      ? "bg-slate-800 text-white"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-50"
                  }`}
                >
                  {s}%
                </button>
              ))}
            </div>
          </div>

          {/* Budget */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">Budget to deploy</span>
              {!isEditingBudget ? (
                <button
                  onClick={startBudgetEdit}
                  disabled={phase !== "idle"}
                  className="flex items-center gap-1 text-sm font-bold text-slate-800 hover:text-slate-600 transition-colors group disabled:opacity-50"
                >
                  {fmt(budget)}
                  <svg
                    className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a2 2 0 01-1.414.586H9v-2a2 2 0 01.586-1.414z"
                    />
                  </svg>
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-400">$</span>
                  <input
                    ref={inputRef}
                    value={budgetInput}
                    onChange={(e) => setBudgetInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitBudget(false);
                      if (e.key === "Escape") setIsEditingBudget(false);
                    }}
                    className="w-20 text-xs border border-slate-300 rounded px-1.5 py-0.5 text-right focus:outline-none focus:border-slate-500"
                    autoFocus
                  />
                </div>
              )}
            </div>
            {isEditingBudget && (
              <div className="flex gap-1.5 mt-1.5">
                <button
                  onClick={() => commitBudget(true)}
                  className="flex-1 text-xs py-1 px-2 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  One-time
                </button>
                <button
                  onClick={() => commitBudget(false)}
                  className="flex-1 text-xs py-1 px-2 rounded bg-slate-800 text-white hover:bg-slate-700 transition-colors"
                >
                  Always
                </button>
                <button
                  onClick={() => setIsEditingBudget(false)}
                  className="text-xs py-1 px-2 text-slate-400 hover:text-slate-600"
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          {/* Qualifying stats */}
          <div className="pt-1 border-t border-slate-100">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500">Invoices qualifying</span>
              <span className="font-semibold text-slate-700 tabular-nums">
                {stats.qualifyingCount.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500">AP eligible</span>
              <span className="font-semibold text-slate-700">{fmt(stats.qualifyingAmt)}</span>
            </div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500">Budget deploys</span>
              <span className="font-semibold text-slate-700">
                {fmt(stats.deployedAmt)}
                {stats.budgetConstrained && (
                  <span className="text-slate-400 font-normal"> (budget capped)</span>
                )}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Est. yield</span>
              <span className="font-semibold text-green-700">
                ${stats.deployedYield.toLocaleString()}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action / progress card */}
      <Card
        className={`shadow-sm border-slate-100 transition-all ${
          phase === "done" ? "border-green-200 bg-green-50/40" : ""
        }`}
      >
        <CardContent className="px-5 py-4">
          {phase === "idle" && (
            <>
              <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                DiscoRun will scan all {TOTAL_INVOICE_COUNT.toLocaleString()} invoices and deploy
                early payments for every supplier whose terms meet your parameters.
              </p>
              <Button
                onClick={onReview}
                className="w-full bg-slate-800 hover:bg-slate-700 text-white font-semibold"
              >
                Review &amp; deploy
              </Button>
            </>
          )}

          {phase === "reviewing" && (
            <>
              <div className="text-sm font-semibold text-slate-700 mb-3">Deployment preview</div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-xs py-2 border-b border-slate-100">
                  <span className="text-slate-500">Invoices</span>
                  <span className="font-semibold text-slate-800">
                    {stats.deployedCount.toLocaleString()} of{" "}
                    {stats.qualifyingCount.toLocaleString()} qualifying
                  </span>
                </div>
                <div className="flex justify-between text-xs py-2 border-b border-slate-100">
                  <span className="text-slate-500">Amount</span>
                  <span className="font-semibold text-slate-800">{fmt(stats.deployedAmt)}</span>
                </div>
                <div className="flex justify-between text-xs py-2 border-b border-slate-100">
                  <span className="text-slate-500">Blended APR</span>
                  <span className="font-semibold text-slate-800">
                    {stats.blendedApr.toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between text-xs py-2">
                  <span className="text-slate-500">Est. yield</span>
                  <span className="font-semibold text-green-700">
                    ${stats.deployedYield.toLocaleString()}
                  </span>
                </div>
              </div>

              {stats.budgetConstrained && (
                <div className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mb-4">
                  Budget of {fmt(budget)} covers {stats.deployedCount.toLocaleString()} of{" "}
                  {stats.qualifyingCount.toLocaleString()} qualifying invoices. Increase budget to
                  deploy all {fmt(stats.qualifyingAmt)}.
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={onCancelReview}
                  className="flex-1 text-slate-500 hover:text-slate-700 border border-slate-200"
                >
                  Cancel
                </Button>
                <Button
                  onClick={onConfirmDeploy}
                  className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold"
                >
                  Confirm →
                </Button>
              </div>
            </>
          )}

          {phase === "deploying" && (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                <span className="text-sm font-semibold text-slate-700">Deploying…</span>
              </div>
              <div className="text-xs text-slate-500 mb-1">
                {deployedCount.toLocaleString()} of {stats.deployedCount.toLocaleString()} payments
                initiated
              </div>
              <Progress value={deployProgress} className="h-1.5 mb-3" />
              <div className="text-xs text-slate-400">
                {fmt(
                  Math.round((deployedCount / Math.max(stats.deployedCount, 1)) * stats.deployedAmt)
                )}{" "}
                deployed so far
              </div>
            </>
          )}

          {phase === "done" && (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-sm font-semibold text-green-700">Deployment complete</span>
              </div>
              <div className="space-y-1.5 mb-3">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Invoices deployed</span>
                  <span className="font-semibold text-slate-700">
                    {stats.deployedCount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Amount</span>
                  <span className="font-semibold text-slate-700">{fmt(stats.deployedAmt)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Yield earned</span>
                  <span className="font-semibold text-green-700">
                    ${stats.deployedYield.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg border border-green-100 text-xs text-slate-600 leading-relaxed">
                Suppliers will receive early payment within 24 hours. Standard terms apply for all
                other invoices.
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── ActCard ─────────────────────────────────────────────────────────────────

function ActCard({
  negotiationPhase,
  contactedIds,
  contactedCount,
  onNegotiate,
}: {
  negotiationPhase: NegotiationPhase;
  contactedIds: Set<number>;
  contactedCount: number;
  onNegotiate: () => void;
}) {
  const signed = NEGOTIATION_TARGETS.filter(
    (t) => contactedIds.has(t.id) && t.agreementType !== null
  );

  return (
    <Card
      className={`shadow-sm border-slate-100 transition-all ${
        negotiationPhase === "done" ? "border-blue-200 bg-blue-50/30" : ""
      }`}
    >
      <CardContent className="px-5 py-4">
        {negotiationPhase === "idle" && (
          <>
            <div className="text-sm font-semibold text-slate-700 mb-1">
              {NEGOTIATION_TARGETS.length} suppliers without terms
            </div>
            <div className="text-xs text-slate-500 mb-3">
              {fmt(NEGOTIATION_TARGETS.reduce((s, t) => s + t.amount, 0))} in AP available for
              optimization. DiscoRun can negotiate early payment terms on your behalf.
            </div>
            <div className="flex flex-col gap-1.5 mb-4">
              {NEGOTIATION_TARGETS.slice(0, 3).map((t) => (
                <div key={t.id} className="flex items-center justify-between text-xs">
                  <span className="text-slate-600 truncate max-w-[160px]">{t.name}</span>
                  <span className="text-slate-400 ml-2">{fmt(t.amount)}</span>
                </div>
              ))}
              <div className="text-xs text-slate-400 mt-0.5">
                + {NEGOTIATION_TARGETS.length - 3} more suppliers
              </div>
            </div>
            <Button
              onClick={onNegotiate}
              variant="outline"
              className="w-full border-blue-200 text-blue-700 hover:bg-blue-50 font-semibold text-sm"
            >
              Start Negotiations
            </Button>
          </>
        )}

        {negotiationPhase === "negotiating" && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-sm font-semibold text-slate-700">Reaching out…</span>
            </div>
            <div className="text-xs text-slate-500 mb-3">
              {contactedCount} of {NEGOTIATION_TARGETS.length} suppliers contacted
            </div>
            <div className="flex flex-col gap-2 max-h-48 overflow-hidden">
              {NEGOTIATION_TARGETS.map((t) => (
                <div key={t.id} className="flex items-center gap-2">
                  <div
                    className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors duration-300 ${
                      contactedIds.has(t.id) ? "bg-blue-500" : "bg-slate-200"
                    }`}
                  />
                  <span
                    className={`text-xs transition-colors duration-300 truncate ${
                      contactedIds.has(t.id) ? "text-slate-700" : "text-slate-400"
                    }`}
                  >
                    {t.name}
                  </span>
                  {contactedIds.has(t.id) && (
                    <span className="ml-auto text-xs text-blue-600 font-medium whitespace-nowrap">
                      {t.potentialApr} APR
                    </span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {negotiationPhase === "done" && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="text-sm font-semibold text-blue-700">Negotiations complete</span>
            </div>
            <div className="text-xs text-slate-500 mb-3">
              {signed.length} agreements signed · {NEGOTIATION_TARGETS.length - signed.length}{" "}
              pending
            </div>
            <div className="flex flex-col gap-1.5 mb-3">
              {signed.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-xs">
                  <span className="text-slate-700 font-medium truncate max-w-[140px]">
                    {t.name}
                  </span>
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full font-medium ml-2 whitespace-nowrap ${
                      t.agreementType === "dynamic"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {t.agreementType === "dynamic" ? "Dynamic" : "One-time"}
                  </span>
                </div>
              ))}
            </div>
            <div className="p-3 bg-white rounded-lg border border-blue-100 text-xs text-slate-600 leading-relaxed">
              Dynamic agreements auto-renew each billing cycle. One-time agreements apply to the
              current invoice only.
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── DemoPage ─────────────────────────────────────────────────────────────────

export default function DemoPage() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [aprFloor, setAprFloor] = useState(1.5);
  const [budget, setBudget] = useState(BUDGET_DEFAULT);
  const [budgetIsOneTime, setBudgetIsOneTime] = useState(false);
  const [deployedCount, setDeployedCount] = useState(0);
  const [activeTab, setActiveTab] = useState<"qualifying" | "all" | "suppliers">("qualifying");
  const [negotiationPhase, setNegotiationPhase] = useState<NegotiationPhase>("idle");
  const [contactedIds, setContactedIds] = useState<Set<number>>(new Set());
  const [contactedCount, setContactedCount] = useState(0);

  const stats = getDeployStats(aprFloor, budget);

  // Counter animation for deploy
  useEffect(() => {
    if (phase !== "deploying") return;
    const target = stats.deployedCount;
    if (deployedCount >= target) {
      const t = setTimeout(() => setPhase("done"), 600);
      return () => clearTimeout(t);
    }
    const increment = Math.max(1, Math.ceil(target / 40));
    const t = setTimeout(() => {
      setDeployedCount((c) => Math.min(c + increment, target));
    }, 60);
    return () => clearTimeout(t);
  }, [phase, deployedCount, stats.deployedCount]);

  // Negotiation animation
  useEffect(() => {
    if (negotiationPhase !== "negotiating") return;
    if (contactedCount >= NEGOTIATION_TARGETS.length) {
      const t = setTimeout(() => setNegotiationPhase("done"), 600);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      const target = NEGOTIATION_TARGETS[contactedCount];
      setContactedIds((prev) => new Set([...prev, target.id]));
      setContactedCount((c) => c + 1);
    }, 900);
    return () => clearTimeout(t);
  }, [negotiationPhase, contactedCount]);

  function handleReset() {
    setPhase("idle");
    setDeployedCount(0);
    setNegotiationPhase("idle");
    setContactedIds(new Set());
    setContactedCount(0);
    if (budgetIsOneTime) {
      setBudget(BUDGET_DEFAULT);
      setBudgetIsOneTime(false);
    }
  }

  const qualifyingInSample = SAMPLE_INVOICES.filter((i) => i.aprRate >= aprFloor && i.aprRate > 0);
  const tableInvoices = activeTab === "qualifying" ? qualifyingInSample : SAMPLE_INVOICES;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 font-sans">
      {/* Header */}
      <header className="border-b border-slate-100 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
              <span className="text-white text-xs font-bold">D</span>
            </div>
            <span className="font-semibold text-slate-800 text-sm tracking-tight">DiscoRun</span>
            <Badge variant="outline" className="text-xs text-slate-400 border-slate-200 ml-1">
              Demo
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400">Apex Precision Systems</span>
            {(phase === "done" ||
              phase === "reviewing" ||
              negotiationPhase === "done" ||
              budgetIsOneTime) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="text-xs text-slate-400 hover:text-slate-600"
              >
                Reset demo
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Hero metrics */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800 font-display mb-1">
            Accounts Payable Overview
          </h1>
          <p className="text-slate-500 text-sm">
            Next 6 weeks · {TOTAL_INVOICE_COUNT.toLocaleString()} invoices outstanding
          </p>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-8">
          {/* Total AP */}
          <Card className="shadow-sm border-slate-100">
            <CardContent className="px-5 py-4">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">
                Total Outstanding AP
              </div>
              <div className="text-2xl font-bold text-slate-800 font-display">
                {fmt(TOTAL_AP_6WK)}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                across {TOTAL_INVOICE_COUNT.toLocaleString()} invoices
              </div>
            </CardContent>
          </Card>

          {/* Qualifying — live */}
          <Card className="shadow-sm border-green-100 bg-green-50/40">
            <CardContent className="px-5 py-4">
              <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">
                Qualifying at {aprFloor}%+
              </div>
              <div className="text-2xl font-bold text-slate-800 font-display">
                {stats.qualifyingCount.toLocaleString()}
              </div>
              <div className="text-xs text-green-600 mt-0.5">
                {fmt(stats.qualifyingAmt)} ·{" "}
                {((stats.qualifyingAmt / TOTAL_AP_6WK) * 100).toFixed(0)}% of AP
              </div>
            </CardContent>
          </Card>

          {/* Budget */}
          <Card className="shadow-sm border-slate-100">
            <CardContent className="px-5 py-4">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">
                Budget to Deploy
              </div>
              <div className="text-2xl font-bold text-slate-800 font-display">{fmt(budget)}</div>
              <div className="text-xs text-slate-400 mt-0.5">
                {budgetIsOneTime ? "one-time override" : "available to deploy"}
              </div>
            </CardContent>
          </Card>

          {/* Est. yield — live */}
          <Card
            className={`shadow-sm border-slate-100 transition-all ${
              phase === "done" ? "border-orange-200 bg-orange-50/40" : ""
            }`}
          >
            <CardContent className="px-5 py-4">
              <div
                className={`text-xs font-semibold uppercase tracking-wide mb-1 ${
                  phase === "done" ? "text-orange-600" : "text-slate-400"
                }`}
              >
                Est. Yield This Cycle
              </div>
              <div className="text-2xl font-bold text-slate-800 font-display">
                $
                {phase === "done"
                  ? stats.deployedYield.toLocaleString()
                  : stats.deployedYield.toLocaleString()}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                {phase === "done"
                  ? `earned · ${stats.blendedApr.toFixed(2)}% blended APR`
                  : `at ${stats.blendedApr.toFixed(2)}% blended APR`}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main content: 65/35 split */}
        <div className="grid grid-cols-[1fr_360px] gap-6">
          {/* Left: chart + table */}
          <div className="flex flex-col gap-6">
            {/* Weekly chart */}
            <Card className="shadow-sm border-slate-100">
              <CardHeader className="px-6 pt-5 pb-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700">AP Due by Week</span>
                  <span className="text-xs text-slate-400">6-week view · $23.4M total</span>
                </div>
              </CardHeader>
              <CardContent className="px-6 pb-5">
                <WeeklyChart deployed={phase === "done"} />
              </CardContent>
            </Card>

            {/* Invoice table */}
            <Card className="shadow-sm border-slate-100">
              <CardHeader className="px-6 pt-5 pb-0">
                <div className="flex items-center gap-1 border-b border-slate-100 pb-0">
                  <button
                    onClick={() => setActiveTab("qualifying")}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                      activeTab === "qualifying"
                        ? "border-slate-800 text-slate-800"
                        : "border-transparent text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    Qualifying
                    <span
                      className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${activeTab === "qualifying" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-400"}`}
                    >
                      {qualifyingInSample.length}
                    </span>
                  </button>
                  <button
                    onClick={() => setActiveTab("all")}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                      activeTab === "all"
                        ? "border-slate-800 text-slate-800"
                        : "border-transparent text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    All Invoices
                    <span
                      className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${activeTab === "all" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-400"}`}
                    >
                      {SAMPLE_INVOICES.length}
                    </span>
                  </button>
                  <button
                    onClick={() => setActiveTab("suppliers")}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                      activeTab === "suppliers"
                        ? "border-slate-800 text-slate-800"
                        : "border-transparent text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    Suppliers
                    <span
                      className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${activeTab === "suppliers" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-400"}`}
                    >
                      {SUPPLIER_PROFILES.length}
                    </span>
                  </button>
                </div>
              </CardHeader>
              <CardContent className="px-6 pb-5 pt-4">
                {activeTab === "suppliers" ? (
                  <SupplierExplorer />
                ) : (
                  <>
                    <InvoiceTable invoices={tableInvoices} aprFloor={aprFloor} phase={phase} />
                    <div className="mt-3 text-xs text-slate-400 text-center">
                      Showing {tableInvoices.length} of {TOTAL_INVOICE_COUNT.toLocaleString()}{" "}
                      invoices
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right column */}
          <div className="flex flex-col gap-4">
            <RightPanel
              phase={phase}
              aprFloor={aprFloor}
              onAprChange={setAprFloor}
              budget={budget}
              onBudgetChange={(v, isOneTime) => {
                setBudget(v);
                setBudgetIsOneTime(isOneTime);
              }}
              deployedCount={deployedCount}
              onReview={() => setPhase("reviewing")}
              onCancelReview={() => setPhase("idle")}
              onConfirmDeploy={() => {
                setPhase("deploying");
                setDeployedCount(0);
              }}
            />
            <ActCard
              negotiationPhase={negotiationPhase}
              contactedIds={contactedIds}
              contactedCount={contactedCount}
              onNegotiate={() => {
                setNegotiationPhase("negotiating");
                setContactedIds(new Set());
                setContactedCount(0);
              }}
            />
          </div>
        </div>
      </main>

      <style>{`
        .font-display { font-family: 'Space Grotesk', system-ui, sans-serif; }
      `}</style>
    </div>
  );
}
